import {
  ESdChangeReason,
  ESourceType,
  ESymbolCategory,
  IAction,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { IActionPanelStatesCanBeUpdatedFromChild, SelectboxInputParameter } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

export interface ISaveSourceSymbolsGroupProps {
  action?: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

export interface ISaveSourceSymbolsGroupState {}

class SaveSourceSymbolsGroup extends PureSdComponent<
  ISaveSourceSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ISaveSourceSymbolsGroupState
> {
  private static readonly MANUEL_MAPPING = { param1: "folderPath", param2: "filePath", param3: "base64Content" };

  private static readonly GOOGLE_FILE_MAPPING = {
    param1: "clientEmail",
    param2: "privateKey",
    param3: "tokenUri",
    param4: "folderId",
    param5: "fileId"
  };

  private static readonly AWS_FILE_MAPPING = {
    param1: "awsAccessKeyId",
    param2: "awsSecretAccessKey",
    param3: "bucketName",
    param4: "s3uri"
  };

  private static readonly YOUTUBE_MAPPING = {
    param1: "url"
  };

  private static readonly WEBSITE_MAPPING = {
    param1: "url"
  };

  // //assuming its either string or a dict with header that's gonna be vectored
  // private static readonly ACTION_RESULT_MAPPING = {
  //   param1: "ActionName",
  //   param2: "ActionResult",
  //   param3: "DeleteOlderRecords",
  //   param4: "JQSchema",
  //   param5: "PrimaryKeys"
  // };

  constructor(props) {
    super(props);
  }

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const params = _.cloneDeep(this.props.parameters);
    const param = params.find((param) => param.name === parameter.name);

    if (param) param.symbol = symbol;

    if (param?.name === "sourceType") {
      for (let i = 0; i < params.length; i++) {
        const element = params[i];
        if (!element.name.includes("param")) continue;

        element.symbol = {
          category: ESymbolCategory.fixed,
          isFixed: true,
          label: "Fixed Value",
          fixedValue: undefined,
          description: ""
        };
      }
    }

    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  getStaticParameters = () => {
    const params = this.props.parameters.filter((param) => param.name.includes("param") === false);

    return params.map((parameter) => {
      let selectboxParameter: SelectboxInputParameter | undefined;

      if (parameter.name === "sourceType") {
        selectboxParameter = {
          name: parameter.name,
          options: [
            { label: "Manual", value: ESourceType.Manuel.toString(), isDefault: true },
            { label: "Google Drive", value: ESourceType.GoogleDrive.toString() },
            { label: "AWS", value: ESourceType.Aws.toString() },
            { label: "YouTube", value: ESourceType.YouTube.toString() },
            { label: "Website", value: ESourceType.Website.toString() }
            // { label: "Action Result", value: ESourceType.Action.toString() }
          ]
        };
      }
      return this.renderParameter(parameter.displayName ?? parameter.name, parameter, selectboxParameter);
    });
  };

  getDynamicParameters = () => {
    const sourceType = this.props.parameters.find((param) => param.name === "sourceType")?.symbol.fixedValue;
    let parameterMapping: { param1: string; param2?: string; param3?: string; param4?: string; param5?: string } =
      SaveSourceSymbolsGroup.MANUEL_MAPPING;
    switch (sourceType?.toString()) {
      case ESourceType.Manuel.toString(): {
        parameterMapping = SaveSourceSymbolsGroup.MANUEL_MAPPING;

        break;
      }
      case ESourceType.GoogleDrive.toString(): {
        parameterMapping = SaveSourceSymbolsGroup.GOOGLE_FILE_MAPPING;

        break;
      }
      case ESourceType.Aws.toString(): {
        parameterMapping = SaveSourceSymbolsGroup.AWS_FILE_MAPPING;

        break;
      }
      case ESourceType.YouTube.toString(): {
        parameterMapping = SaveSourceSymbolsGroup.YOUTUBE_MAPPING;

        break;
      }
      case ESourceType.Website.toString(): {
        parameterMapping = SaveSourceSymbolsGroup.WEBSITE_MAPPING;

        break;
      }
      // case ESourceType.Action.toString(): {
      //   parameterMapping = SaveSourceSymbolsGroup.ACTION_RESULT_MAPPING;
      //
      //   break;
      // }
    }

    return (
      <>
        {this.props.parameters
          .filter((param) => parameterMapping[param.name])
          .map((param) => this.renderParameter(parameterMapping[param.name], param))}
      </>
    );
  };

  renderParameter = (
    parameterDisplayName: string,
    parameter: IActionParameter,
    selectboxParameter?: SelectboxInputParameter
  ) => {
    if (parameter.isOptionalParameter && !parameter.isExternalVisible) return <></>;
    if (selectboxParameter?.isDynamic === true) return <></>;

    return (
      <div className={parameter.isDeletedOnSource === true ? "isDeletedOnSource" : ""} key={Guid.create().toString()}>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          <ActionParameterTitle
            parameter={{ name: parameterDisplayName, Typename: parameter.Typename, cardinality: parameter.cardinality }}
            showTypeChips={selectboxParameter === undefined}
          />
        </span>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={parameter.symbol}
          valueType={parameter.Typename}
          targetCardinality={parameter.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(parameter, symbol, changeReason)
          }
          parameterOptions={selectboxParameter?.options}
          bindingParameter={parameter}
          bindingLabel={parameterDisplayName}
        />
      </div>
    );
  };

  render(): ReactNode {
    return (
      <>
        {this.getStaticParameters()}
        {this.getDynamicParameters()}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    );
  };
}

const saveSourceSymbolsGroup = withSD<ISaveSourceSymbolsGroupProps>(SaveSourceSymbolsGroup);
export { saveSourceSymbolsGroup as SaveSourceSymbolsGroup };
